/**
 * Google Sheets integration service for LiftForge
 * Handles sync of workout history and configuration to Google Sheets
 * Ported from index.html lines 1011-1209
 */

import { AppState, HistoryEntry, HistoryExercise } from '@/lib/types';
import { SPREADSHEET_NAME } from '@/lib/constants';

const SHEETS_API = 'https://sheets.googleapis.com/v4/spreadsheets';
const DRIVE_API = 'https://www.googleapis.com/drive/v3/files';

interface SheetsServiceType {
  accessToken: string | null;
  spreadsheetId: string | null;
  _fetch: (url: string, options?: RequestInit) => Promise<any>;
  findSpreadsheet: () => Promise<string | null>;
  createSpreadsheet: () => Promise<string>;
  _getValues: (range: string) => Promise<string[][]>;
  _clearAndWrite: (range: string, values: string[][]) => Promise<void>;
  loadAll: () => Promise<Partial<AppState>>;
  resetSpreadsheet: () => Promise<void>;
  saveAll: (state: AppState) => Promise<void>;
}

/**
 * Flatten history entries into 2D array for Sheets
 * Format: [Date, Workout, Exercise, Set #, Reps, Weight, Intensity, Warmup]
 */
export function flattenHistory(history: HistoryEntry[]): string[][] {
  const rows: string[][] = [['Date', 'Workout', 'Exercise', 'Set #', 'Reps', 'Weight', 'Intensity', 'Warmup']];

  history.forEach((entry) => {
    (entry.exercises || []).forEach((ex) => {
      (ex.sets || []).forEach((set, si) => {
        const rir = set.rir !== undefined ? set.rir : 2;
        const warmup = set.warmup ? 'Y' : 'N';
        rows.push([
          entry.date || '',
          entry.workoutName || 'Unknown',
          ex.name || '',
          String(si + 1),
          String(set.reps || 0),
          String(set.weight || 0),
          String(rir),
          warmup,
        ]);
      });
    });
  });

  return rows;
}

/**
 * Parse flat history rows back into structured entries grouped by date+workout
 */
export function parseHistory(rows: string[][]): HistoryEntry[] {
  const groups: Record<string, any> = {};

  rows.forEach((r) => {
    // Skip header row
    if (!r[0] || r[0] === 'Date') return;

    const key = `${r[0]}|${r[1] || ''}`;
    if (!groups[key]) {
      groups[key] = {
        date: r[0],
        workoutId: null,
        workoutName: r[1] || 'Unknown',
        wasOverride: false,
        exercises: {} as Record<string, any>,
      };
    }

    const exName = r[2] || '';
    if (!groups[key].exercises[exName]) {
      groups[key].exercises[exName] = {
        exerciseId: exName,
        name: exName,
        sets: [] as any[],
      };
    }

    // Support both old (7-col effort) and new (8-col intensity+warmup) format
    const rir = r.length >= 8 && r[6] != null && r[6] !== '' ? parseInt(r[6], 10) : 2;
    const warmup = r.length >= 8 && r[7] === 'Y';

    groups[key].exercises[exName].sets.push({
      reps: parseInt(r[4], 10) || 0,
      weight: parseFloat(r[5]) || 0,
      rir: rir,
      warmup: warmup,
    });
  });

  return Object.values(groups).map((g) => ({
    date: g.date,
    workoutId: g.workoutId,
    workoutName: g.workoutName,
    wasOverride: g.wasOverride,
    exercises: Object.values(g.exercises) as HistoryExercise[],
  }));
}

/**
 * Merge local and remote state for conflict resolution during reconnect
 * Uses timestamps to determine which config wins (last-write-wins)
 * Unions history entries by date+workoutId (keeps entry with more exercises)
 */
export function mergeData(
  local: AppState,
  remote: AppState,
  localTimestamp: number,
  remoteTimestamp: number,
): AppState {
  // History: union by date+workoutId, keep entry with more exercises
  const histMap = new Map<string, HistoryEntry>();

  // Add remote entries first
  (remote.history || []).forEach((h) => {
    const key = `${h.date}|${h.workoutId}`;
    histMap.set(key, h);
  });

  // Add/override with local entries (local wins on ties)
  (local.history || []).forEach((h) => {
    const key = `${h.date}|${h.workoutId}`;
    const existing = histMap.get(key);
    if (!existing || (h.exercises || []).length > (existing.exercises || []).length) {
      histMap.set(key, h);
    }
  });

  const history = Array.from(histMap.values()).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );

  // WeightLog: union by date, local wins ties
  const weightMap = new Map<string, any>();
  (remote.weightLog || []).forEach((w: any) => {
    weightMap.set(w.date, w);
  });
  (local.weightLog || []).forEach((w: any) => {
    weightMap.set(w.date, w);
  });

  const weightLog = Array.from(weightMap.values()).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );

  // Config: last-edited-wins via timestamp
  const localTs = localTimestamp;
  const remoteTs = remoteTimestamp;
  const cfg = localTs >= remoteTs ? local : remote;

  return {
    ...cfg,
    history,
    weightLog: weightLog || [],
    configTimestamp: Math.max(localTs, remoteTs),
  };
}

/**
 * Create sheetsService singleton
 * Manages Google Sheets API interactions
 */
export function createSheetsService(): SheetsServiceType {
  const sheetsService: SheetsServiceType = {
    accessToken: null,
    spreadsheetId: null,

    async _fetch(url: string, options?: RequestInit) {
      const opts = options || {};
      const res = await fetch(url, {
        ...opts,
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          ...(opts.headers || {}),
        },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err.error || {}).message || `API error ${res.status}`);
      }

      return res.json();
    },

    async findSpreadsheet() {
      const q1 = encodeURIComponent(
        `name='${SPREADSHEET_NAME}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
      );
      const data1 = await this._fetch(`${DRIVE_API}?q=${q1}&spaces=drive&fields=files(id,name)`);
      if ((data1.files || [])[0]) return data1.files[0].id;

      const LEGACY_NAME = 'LiftForge';
      if ((LEGACY_NAME as string) !== SPREADSHEET_NAME) {
        const q2 = encodeURIComponent(
          `name='${LEGACY_NAME}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
        );
        const data2 = await this._fetch(`${DRIVE_API}?q=${q2}&spaces=drive&fields=files(id,name)`);
        if ((data2.files || [])[0]) return data2.files[0].id;
      }

      return null;
    },

    async createSpreadsheet() {
      const data = await this._fetch(SHEETS_API, {
        method: 'POST',
        body: JSON.stringify({
          properties: { title: SPREADSHEET_NAME },
          sheets: [
            { properties: { title: 'config', index: 0 } },
            { properties: { title: 'history', index: 1 } },
            { properties: { title: 'weight', index: 2 } },
          ],
        }),
      });

      this.spreadsheetId = data.spreadsheetId;

      await this._fetch(`${SHEETS_API}/${this.spreadsheetId}/values:batchUpdate`, {
        method: 'POST',
        body: JSON.stringify({
          valueInputOption: 'RAW',
          data: [
            { range: 'config!A1:B1', values: [['key', 'value']] },
            {
              range: 'history!A1:H1',
              values: [['Date', 'Workout', 'Exercise', 'Set #', 'Reps', 'Weight', 'Intensity', 'Warmup']],
            },
            { range: 'weight!A1:C1', values: [['Date', 'Weight (lbs)', 'Body Fat %']] },
          ],
        }),
      });

      return this.spreadsheetId;
    },

    async _getValues(range: string) {
      const d = await this._fetch(`${SHEETS_API}/${this.spreadsheetId}/values/${encodeURIComponent(range)}`);
      return d.values || [];
    },

    async _clearAndWrite(range: string, values: string[][]) {
      await this._fetch(`${SHEETS_API}/${this.spreadsheetId}/values/${encodeURIComponent(range)}:clear`, {
        method: 'POST',
        body: '{}',
      });

      if (values.length > 0) {
        await this._fetch(
          `${SHEETS_API}/${this.spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=RAW`,
          {
            method: 'PUT',
            body: JSON.stringify({ range, values }),
          },
        );
      }
    },

    async loadAll() {
      const [configRows, historyRows, weightRows] = await Promise.all([
        this._getValues('config!A2:B200'),
        this._getValues('history!A2:H50000'),
        this._getValues('weight!A2:C1000'),
      ]);

      // Try legacy workouts tab for backward compatibility
      let legacyWorkoutRows: any[] = [];
      try {
        const res = await fetch(
          `${SHEETS_API}/${this.spreadsheetId}/values/${encodeURIComponent('workouts!A2:B1000')}`,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (res.ok) {
          const d = await res.json();
          legacyWorkoutRows = d.values || [];
        }
      } catch {
        // Ignore network errors
      }

      // Parse config key-value pairs
      const config: Record<string, any> = {};
      configRows.forEach((r) => {
        try {
          config[r[0]] = JSON.parse(r[1]);
        } catch (e) {
          console.warn('Config parse failed for', r[0]);
          config[r[0]] = r[1];
        }
      });

      // Load workouts: prefer config.workouts, fall back to legacy workouts tab
      let workouts = config.workouts || {};
      if (Object.keys(workouts).length === 0 && legacyWorkoutRows.length > 0) {
        legacyWorkoutRows.forEach((r) => {
          try {
            workouts[r[0]] = JSON.parse(r[1]);
          } catch (e) {
            // Ignore parse errors
          }
        });
      }

      // Detect history format: old format has 2 columns (date, json), new has 7+ columns
      let history: HistoryEntry[];
      if (historyRows.length > 0 && historyRows[0].length <= 2) {
        // Legacy format: parse JSON blobs
        history = historyRows
          .map((r) => {
            try {
              return JSON.parse(r[1]);
            } catch (e) {
              return null;
            }
          })
          .filter(Boolean);
      } else {
        // New flat format
        history = parseHistory(historyRows);
      }

      // Restore workoutId references by matching workout names
      history.forEach((h) => {
        if (!h.workoutId || !workouts[h.workoutId]) {
          Object.entries(workouts).forEach(([id, wo]: [string, any]) => {
            if (wo.name === h.workoutName) {
              h.workoutId = id;
            }
          });
        }
      });

      // Parse weight log
      const weightLog = weightRows
        .map((r) => {
          const bf = r[2] ? parseFloat(r[2]) : null;
          return {
            date: r[0],
            weight: parseFloat(r[1]),
            bodyFat: !isNaN(bf) && bf > 0 ? bf : null,
          };
        })
        .filter((w) => !isNaN(w.weight));

      return {
        scheduleType: config.scheduleType || 'weekly',
        weeklySchedule: config.weeklySchedule,
        cadenceSchedule: config.cadenceSchedule || {
          restMode: 'manual' as const,
          autoRestEvery: 3,
          rotation: [],
          currentIndex: 0,
        },
        multiWeekSchedule: config.multiWeekSchedule || undefined,
        workouts,
        history,
        weightLog,
        unitPreference: config.weightUnit ? (config.weightUnit === 'kg' ? 'kg' : 'lbs') : 'lbs',
        customBodyParts: config.customBodyParts || [],
        customExCatMap: config.customExCatMap || {},
        configTimestamp: Number(config.configLastModified) || 0,
      };
    },

    async resetSpreadsheet() {
      if (!this.spreadsheetId) return;

      await Promise.all([
        this._clearAndWrite('config!A1:B200', [['key', 'value']]),
        this._clearAndWrite('history!A1:H50000', [
          ['Date', 'Workout', 'Exercise', 'Set #', 'Reps', 'Weight', 'Intensity', 'Warmup'],
        ]),
        this._clearAndWrite('weight!A1:C1000', [['Date', 'Weight (lbs)', 'Body Fat %']]),
      ]);
    },

    async saveAll(state: AppState) {
      const configVals = [
        ['key', 'value'],
        ['scheduleType', JSON.stringify(state.scheduleType)],
        ['weeklySchedule', JSON.stringify(state.weeklySchedule)],
        ['cadenceSchedule', JSON.stringify(state.cadenceSchedule)],
        ['multiWeekSchedule', JSON.stringify(state.multiWeekSchedule)],
        ['workouts', JSON.stringify(state.workouts)],
        ['weightUnit', JSON.stringify(state.unitPreference || 'lbs')],
        ['customBodyParts', JSON.stringify(state.customBodyParts || [])],
        ['customExCatMap', JSON.stringify(state.customExCatMap || {})],
        ['configLastModified', JSON.stringify(state.configTimestamp || 0)],
      ];

      const historyVals = flattenHistory(state.history);
      const unit = state.unitPreference || 'lbs';
      const weightVals = [['Date', `Weight (${unit})`, 'Body Fat %']].concat(
        (state.weightLog || []).map((w: any) => [
          w.date,
          String(w.weight),
          w.bodyFat != null ? String(w.bodyFat) : '',
        ]),
      );

      await Promise.all([
        this._clearAndWrite('config!A1:B200', configVals as any),
        this._clearAndWrite('history!A1:H50000', historyVals),
        this._clearAndWrite('weight!A1:C1000', weightVals),
      ]);
    },
  };

  return sheetsService;
}

export type { SheetsServiceType };
